import React, { useState } from "react";
import {
  Box,
  Button,
  Popover,
  useMediaQuery,
  useTheme,
  Typography,
  IconButton,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import { formatDateForPicker } from "../../utils/formatDate";
import { useTranslation } from "react-i18next";

const DateRangePicker = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const [showBothCalendars, setShowBothCalendars] = useState(false);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setSelectingStart(true); // Always start with selecting start date
    setShowBothCalendars(false); // Start with single calendar
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectingStart(true);
    setShowBothCalendars(false);
  };

  const handleClear = (event) => {
    event.stopPropagation(); // Prevent opening the popover when clicking clear
    onStartDateChange(null);
    onEndDateChange(null);
  };

  const handleDateSelect = (date, isStartDate) => {
    if (isStartDate) {
      onStartDateChange(date);
      if (!endDate || date > endDate) {
        // If no end date or start date is after end date, clear end date
        onEndDateChange(null);
      }
      setSelectingStart(false); // Switch to selecting end date
    } else {
      if (startDate && date < startDate) {
        // If end date is before start date, swap them
        onStartDateChange(date);
        onEndDateChange(startDate);
      } else {
        onEndDateChange(date);
      }
      // Auto-close after selecting end date
      setTimeout(() => handleClose(), 300);
    }
  };

  const open = Boolean(anchorEl);

  // Get today's date at start of day for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Use dynamic format for button display
  const getButtonText = () => {
    if (startDate && endDate) {
      return `${formatDateForPicker(startDate)} - ${formatDateForPicker(
        endDate
      )}`;
    }
    return t("Select Date Range");
  };

  const hasSelectedDates = startDate || endDate;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Button
          onClick={handleOpen}
          sx={{
            border: "1px solid #414958",
            borderRadius: "4px",
            padding: "8px 16px",
            backgroundColor: "#F2F2F3",
            color: "#1F242E",
            fontSize: "16px",
            textTransform: "none",
            minWidth: "240px",
            fontFamily: "Roboto",
            fontWeight: "400",
            justifyContent: "space-between",
            height: "35px",
            position: "relative",
          }}
          endIcon={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {hasSelectedDates && (
                <IconButton
                  size="small"
                  onClick={handleClear}
                  sx={{
                    padding: "2px",
                    color: "#647490",
                    "&:hover": {
                      backgroundColor: "rgba(100, 116, 144, 0.1)",
                      color: "#ff4444",
                    },
                  }}
                >
                  <ClearIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              )}
              <ArrowDropDownIcon sx={{ color: "#183059" }} />
            </Box>
          }
        >
          {getButtonText()}
        </Button>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              mt: 1,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              borderRadius: "8px",
            },
          }}
        >
          <Box
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minWidth: isMobile ? "320px" : "320px",
            }}
          >
            {/* Navigation Header - Show when start date is selected */}
            {startDate && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <IconButton
                  onClick={() => setSelectingStart(true)}
                  sx={{
                    backgroundColor: selectingStart ? "#1976d2" : "transparent",
                    color: selectingStart ? "white" : "#1976d2",
                    "&:hover": {
                      backgroundColor: selectingStart ? "#1565c0" : "rgba(25, 118, 210, 0.1)",
                    },
                  }}
                >
                  <ArrowDropDownIcon sx={{ transform: "rotate(90deg)" }} />
                </IconButton>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectingStart ? t("Select Start Date") : t("Select End Date")}
                </Typography>
                <IconButton
                  onClick={() => setSelectingStart(false)}
                  disabled={!startDate}
                  sx={{
                    backgroundColor: !selectingStart ? "#4caf50" : "transparent",
                    color: !selectingStart ? "white" : startDate ? "#4caf50" : "#ccc",
                    "&:hover": {
                      backgroundColor: !selectingStart ? "#45a049" : startDate ? "rgba(76, 175, 80, 0.1)" : "transparent",
                    },
                  }}
                >
                  <ArrowDropDownIcon sx={{ transform: "rotate(-90deg)" }} />
                </IconButton>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 2,
              }}
            >
              {/* Start Date Calendar - Visible when selecting start */}
              {selectingStart && (
                <Box sx={{ flex: 1 }}>
                  <StaticDatePicker
                    value={startDate}
                    onChange={(date) => handleDateSelect(date, true)}
                    maxDate={today}
                    slotProps={{
                      actionBar: { actions: [] },
                      toolbar: { hidden: true },
                    }}
                    slots={{
                      toolbar: () => null,
                    }}
                    sx={{
                      "& .MuiPickersCalendarHeader-root": {
                        paddingLeft: 1,
                        paddingRight: 1,
                      },
                      "& .MuiDayCalendar-root": {
                        width: "100%",
                      },
                      "& .MuiPickersDay-root": {
                        fontSize: "0.875rem",
                      },
                      "& .MuiPickersDay-root.Mui-selected": {
                        backgroundColor: "#1976d2",
                      },
                    }}
                  />
                </Box>
              )}

              {/* End Date Calendar - Visible when selecting end date */}
              {!selectingStart && (
                <Box sx={{ flex: 1 }}>
                  <StaticDatePicker
                    value={endDate}
                    onChange={(date) => handleDateSelect(date, false)}
                    minDate={startDate || undefined}
                    maxDate={today}
                    disabled={!startDate}
                    slotProps={{
                      actionBar: { actions: [] },
                      toolbar: { hidden: true },
                    }}
                    slots={{
                      toolbar: () => null,
                    }}
                    sx={{
                      "& .MuiPickersCalendarHeader-root": {
                        paddingLeft: 1,
                        paddingRight: 1,
                      },
                      "& .MuiDayCalendar-root": {
                        width: "100%",
                      },
                      "& .MuiPickersDay-root": {
                        fontSize: "0.875rem",
                      },
                      "& .MuiPickersDay-root.Mui-selected": {
                        backgroundColor: "#4caf50",
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePicker;